import React, { useState, useEffect } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useBranding } from "../useBranding";
import {
  Sparkle, ShieldStar, Briefcase, UserCircle, ArrowRight,
  Eye, EyeSlash, Lightning, InstagramLogo, Users,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatErr } from "../api";

/** Per-role visual + copy for the switchable tabs */
const ROLE_TABS = [
  {
    key: "ambassador",
    label: "Creator",
    icon: UserCircle,
    accent: "#EC4899",
    gradient: "from-[#EC4899] to-[#8B5CF6]",
    tagline: "Apply to brand drops, post content, get paid.",
    demoLine: "Try as Creator Demo",
  },
  {
    key: "client",
    label: "Brand",
    icon: Briefcase,
    accent: "#22D3EE",
    gradient: "from-[#22D3EE] to-[#8B5CF6]",
    tagline: "Track your live campus campaigns in real time.",
    demoLine: "Try as Brand Demo",
  },
];

/** Dedicated-workspace variants — Brand / Agency / Creator each get a locked, styled login. */
const VARIANT_CONFIG = {
  brand: {
    role: "client",
    kicker: "Brand Workspace",
    heading: "Welcome back.",
    accent: "Manage your creator campaigns, ambassadors and operations.",
    label: "Brand",
    accentColor: "#22D3EE",
    accentGradient: "from-[#22D3EE] to-[#8B5CF6]",
    icon: Briefcase,
    demoLabel: "Try Brand Demo",
    allowRegister: false,
  },
  agency: {
    role: "client",           // agencies share the Brand backend/UI
    kicker: "Agency Workspace",
    heading: "Campaign Operations for your clients.",
    accent: "Manage creator campaigns for every brand you represent — one console.",
    label: "Agency",
    accentColor: "#A3E635",
    accentGradient: "from-[#A3E635] to-[#22D3EE]",
    icon: Briefcase,
    demoLabel: "Try Agency Demo",
    allowRegister: false,
  },
  creator: {
    role: "ambassador",
    kicker: "Creator Network",
    heading: "Sign in to your creator hub.",
    accent: "Apply to brand drops, submit content, get paid — on your terms.",
    label: "Creator",
    accentColor: "#EC4899",
    accentGradient: "from-[#EC4899] to-[#8B5CF6]",
    icon: UserCircle,
    demoLabel: "Try Creator Demo",
    allowRegister: true,
  },
};

export default function Login({ adminMode = false, variant = null }) {
  const { user, login, demoLogin, register, setUser } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const igToken = searchParams.get("ig_token");
  const igUsername = searchParams.get("ig_username");
  const igFollowers = searchParams.get("ig_followers");
  // Live white-labeling: the URL may include /o/<slug>/... which useBranding
  // auto-parses. When a tenant slug is present, overlay their logo/appName/accent
  // on top of the variant defaults so their team sees a fully branded sign-in.
  const branding = useBranding();

  const cfg = variant ? VARIANT_CONFIG[variant] : null;

  // Effective visuals: tenant branding wins, variant defaults are the fallback.
  const brandAccent = branding?.accent_color || cfg?.accentColor || "#3B82F6";
  const brandLogo = branding?.logo_url || "";
  const brandAppName = branding?.app_name || "CrewHQ";
  const brandHeading = branding?.login_heading || "";
  const brandDescription = branding?.login_description || "";

  // tab: 'ambassador' | 'client' — locked when variant is set
  const [tab, setTab] = useState(cfg?.role || "ambassador");
  // mode within a tab: 'login' | 'register'
  const [mode, setMode] = useState("login");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [regName, setRegName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get("ig_error")) {
      toast.error("Instagram connection failed — please try again.");
    }
  }, [searchParams]);

  // Every route into this page targets a specific role (adminMode, or a
  // variant like /login/brand). Only auto-redirect away when the currently
  // logged-in session ALREADY is that role — otherwise someone logged in as
  // e.g. a creator who opens /admin-login to switch accounts gets bounced
  // straight back to /me before the admin form (or even a demo/credential
  // login attempt) ever renders.
  const targetRole = adminMode ? "admin" : cfg?.role;
  if (user && user !== false && (!targetRole || user.role === targetRole)) {
    const dest = user.role === "admin" ? "/admin" : user.role === "client" ? "/client" : "/me";
    return <Navigate to={dest} replace />;
  }

  const goByRole = (role) => {
    if (role === "admin") nav("/admin");
    else if (role === "client") nav("/client");
    else nav("/me");
  };

  const handleDemo = async (role) => {
    setLoading(true);
    try {
      const u = await demoLogin(role);
      toast.success(`Welcome, ${u.name}!`);
      goByRole(u.role);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== tab) {
        // Soft warn but still route — credentials always win
        toast.message(`Signed in as ${u.role}, not ${tab}. Redirecting…`);
      } else {
        toast.success(`Welcome, ${u.name}!`);
      }
      goByRole(u.role);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const handleInstagramConnect = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/instagram/login-url");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatErr(e));
      setLoading(false);
    }
  };

  const handleCompleteInstagramSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: u } = await api.post("/auth/register-with-instagram", {
        ig_token: igToken, email, password, name: regName,
      });
      setUser(u);
      toast.success(u.message || `Welcome aboard, ${u.name}!`, { duration: 8000 });
      goByRole(u.role);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register({ email, password, name: regName, role: tab });
      if (u.pending_approval || u.message) {
        toast.success(u.message || "Your profile is pending approval from the TYC Team. For urgent help contact support.", { duration: 8000 });
      } else {
        toast.success(`Welcome aboard, ${u.name}!`);
      }
      goByRole(u.role);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const active = ROLE_TABS.find(t => t.key === tab);

  return (
    <div className="app-bg min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-10">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left: brand + admin shortcut */}
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            {brandLogo ? (
              <img src={brandLogo} alt={brandAppName} className="w-12 h-12 rounded-2xl object-cover"
                   style={{ boxShadow: `0 0 24px ${brandAccent}55` }} />
            ) : (
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${cfg?.accentGradient || "from-[#8B5CF6] to-[#EC4899]"} grid place-items-center glow-purple`}>
                {cfg ? <cfg.icon size={22} weight="fill" color="white" /> : <Sparkle size={22} weight="fill" color="white" />}
              </div>
            )}
            <div>
              <div className="text-3xl font-black text-white tracking-tight leading-none" data-testid="login-app-name">
                {brandAppName.endsWith("HQ") && brandAppName.includes("Crew")
                  ? (<>{brandAppName.replace("HQ", "")}<span style={{ color: brandAccent }}>HQ</span></>)
                  : brandAppName}
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted font-bold mt-1">
                {cfg?.kicker || "Creator Economy Control Room"}
              </div>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-[1.05]">
            {brandHeading ? (
              <>{brandHeading}</>
            ) : cfg ? (
              <>{cfg.heading}<br/><span className="bg-gradient-to-r from-[#22D3EE] via-[#A3E635] to-[#EC4899] bg-clip-text text-transparent">{cfg.label} workspace</span></>
            ) : (
              <>Manage creators, campaigns, and{" "}
                <span className="bg-gradient-to-r from-[#22D3EE] via-[#A3E635] to-[#EC4899] bg-clip-text text-transparent">
                  campus ambassadors
                </span>{" "}
                in one place.
              </>
            )}
          </h1>

          <p className="text-muted text-base sm:text-lg">
            {brandDescription || cfg?.accent || "Built for Gen Z energy. Run brand campaigns, track submissions, pay creators — no spreadsheets, no chaos."}
          </p>

          {!variant && (
            <div className="text-xs text-muted">
              Not the right workspace? <a href="/login" className="text-[#22D3EE] font-bold" data-testid="back-to-selector">Choose again</a>
            </div>
          )}
          {variant && (
            <div className="text-xs text-muted" data-testid="variant-hint">
              Signing in as <span className="text-white font-bold">{cfg.label}</span>. Wrong workspace? <a href="/login" className="text-[#22D3EE] font-bold" data-testid="back-to-selector">Choose again</a>
            </div>
          )}

          {adminMode && (
            <button
              onClick={() => handleDemo("admin")}
              disabled={loading}
              data-testid="demo-login-admin"
              className="glass-soft p-4 sm:p-5 text-left hover-lift group disabled:opacity-50 flex items-center gap-4 w-full sm:max-w-md"
            >
              <div className="w-12 h-12 rounded-2xl grid place-items-center bg-gradient-to-br from-[#A3E635] to-[#22D3EE] glow-cyan flex-shrink-0">
                <ShieldStar size={20} weight="fill" color="white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold flex items-center gap-2">
                  Login as Admin
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#A3E635] bg-[#A3E635]/15 border border-[#A3E635]/30 px-2 py-0.5 rounded-full">Restricted</span>
                </div>
                <div className="text-xs text-muted mt-1">Total control of campaigns, creators, and money.</div>
              </div>
              <ArrowRight size={18} weight="bold" className="text-muted group-hover:text-white transition flex-shrink-0" />
            </button>
          )}
        </div>

        {/* Right: Creator / Brand login (locked in variant mode) */}
        <div className="glass p-6 sm:p-8 shadow-deep">
          {igToken && tab === "ambassador" ? (
            <form onSubmit={handleCompleteInstagramSignup} className="space-y-4" data-testid="ig-complete-form">
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-br from-[#8B5CF6]/15 to-[#EC4899]/15 border border-[#8B5CF6]/30">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] grid place-items-center flex-shrink-0">
                  <InstagramLogo size={20} weight="fill" color="white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white font-bold text-sm truncate" data-testid="ig-complete-username">@{igUsername || "connected"}</div>
                  <div className="text-xs text-muted flex items-center gap-1">
                    <Users size={11} weight="bold" /> {Number(igFollowers || 0).toLocaleString("en-IN")} followers · auto-filled from Instagram
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-muted">Your name</label>
                <input
                  required value={regName} onChange={e => setRegName(e.target.value)}
                  placeholder="Aria Mehta" className="input-base mt-2" data-testid="ig-complete-name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-muted">Email</label>
                <input
                  type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@creator.com" className="input-base mt-2" data-testid="ig-complete-email"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-muted">Password</label>
                <input
                  type="password" required minLength={6} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="At least 6 chars"
                  className="input-base mt-2" data-testid="ig-complete-password"
                />
              </div>
              <button
                disabled={loading} type="submit" data-testid="ig-complete-submit"
                className="btn-primary w-full flex items-center justify-center gap-2"
                style={{ background: "linear-gradient(90deg, #8B5CF6 0%, #EC4899 100%)", boxShadow: "0 8px 24px #8B5CF655" }}
              >
                {loading ? "Creating…" : "Finish creating account"} <ArrowRight size={16} weight="bold" />
              </button>
              <p className="text-[11px] text-muted text-center leading-relaxed">
                By creating an account you agree to CrewHQ terms.
              </p>
            </form>
          ) : (
          <>
          {/* Role tabs — hidden when a variant locks the workspace */}
          {!variant && (
          <div className="grid grid-cols-2 gap-2 p-1 bg-white/5 rounded-2xl mb-6">
            {ROLE_TABS.map(t => {
              const Icon = t.icon;
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => { setTab(t.key); setMode("login"); }}
                  data-testid={`role-tab-${t.key}`}
                  className={`relative flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all
                    ${isActive ? "text-white" : "text-muted hover:text-white"}`}
                  style={isActive ? {
                    background: `linear-gradient(135deg, ${t.accent}22 0%, ${t.accent}11 100%)`,
                    boxShadow: `0 0 24px ${t.accent}33, inset 0 0 0 1px ${t.accent}55`,
                  } : {}}
                >
                  <Icon size={16} weight={isActive ? "fill" : "bold"} color={isActive ? t.accent : undefined} />
                  {t.label}
                </button>
              );
            })}
          </div>
          )}

          {/* Tagline for the active tab */}
          <div className="flex items-center gap-2 mb-5 text-xs text-muted">
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: cfg?.accentColor || active.accent, boxShadow: `0 0 8px ${cfg?.accentColor || active.accent}` }}
            />
            {cfg?.accent || active.tagline}
          </div>

          {/* Mode tabs (login / register) — register hidden for brand/agency variants */}
          {(!variant || cfg?.allowRegister) ? (
          <div className="flex gap-2 mb-6">
            <button onClick={() => setMode("login")} data-testid="tab-login"
              className={`flex-1 py-2 rounded-full text-sm font-bold transition ${mode === "login" ? "bg-white/10 text-white" : "text-muted hover:text-white"}`}>
              Sign in
            </button>
            <button onClick={() => setMode("register")} data-testid="tab-register"
              className={`flex-1 py-2 rounded-full text-sm font-bold transition ${mode === "register" ? "bg-white/10 text-white" : "text-muted hover:text-white"}`}>
              Create {(cfg?.label || active.label).toLowerCase()} account
            </button>
          </div>
          ) : null}

          {mode === "login" ? (
            <form onSubmit={handleLogin} className="space-y-4" key={`login-${tab}`}>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-muted">Email</label>
                <input
                  type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder={tab === "client" ? "you@brand.com" : "you@creator.com"}
                  className="input-base mt-2" data-testid={`${tab}-login-email`}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-muted">Password</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"} required value={password}
                    onChange={e => setPassword(e.target.value)} placeholder="••••••••"
                    className="input-base mt-2 pr-12" data-testid={`${tab}-login-password`}
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 mt-1 text-muted hover:text-white">
                    {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <button
                disabled={loading} type="submit"
                data-testid={`${tab}-login-submit`}
                className="btn-primary w-full flex items-center justify-center gap-2"
                style={{ background: `linear-gradient(90deg, ${brandAccent} 0%, ${branding?.secondary_color || "#8B5CF6"} 100%)`,
                         boxShadow: `0 8px 24px ${brandAccent}55` }}
              >
                {loading ? "Signing in…" : `Sign in as ${cfg?.label || active.label}`} <ArrowRight size={16} weight="bold" />
              </button>

              {/* Quick demo within tab */}
              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
                <div className="relative flex justify-center"><span className="bg-[#111827] px-3 text-[10px] uppercase tracking-[0.18em] font-bold text-muted">or</span></div>
              </div>
              <button
                type="button" onClick={() => handleDemo(tab)} disabled={loading}
                data-testid={`${tab}-demo-login`}
                className="btn-ghost w-full flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Lightning size={14} weight="fill" color={cfg?.accentColor || active.accent} />
                {cfg?.demoLabel || active.demoLine}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4" key={`reg-${tab}`}>
              {tab === "ambassador" && (
                <>
                  <button
                    type="button" onClick={handleInstagramConnect} disabled={loading}
                    data-testid="ig-connect-btn"
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-sm text-white disabled:opacity-50 transition"
                    style={{ background: "linear-gradient(90deg, #8B5CF6 0%, #EC4899 100%)", boxShadow: "0 8px 24px #8B5CF655" }}
                  >
                    <InstagramLogo size={18} weight="fill" /> Continue with Instagram
                  </button>
                  <div className="relative my-1">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
                    <div className="relative flex justify-center"><span className="bg-[#111827] px-3 text-[10px] uppercase tracking-[0.18em] font-bold text-muted">or fill manually</span></div>
                  </div>
                </>
              )}
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-muted">
                  {tab === "client" ? "Brand / Company name" : "Your name"}
                </label>
                <input
                  required value={regName} onChange={e => setRegName(e.target.value)}
                  placeholder={tab === "client" ? "Neon Threads" : "Aria Mehta"}
                  className="input-base mt-2" data-testid={`${tab}-register-name`}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-muted">Email</label>
                <input
                  type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder={tab === "client" ? "you@brand.com" : "you@creator.com"}
                  className="input-base mt-2" data-testid={`${tab}-register-email`}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-muted">Password</label>
                <input
                  type="password" required minLength={6} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="At least 6 chars"
                  className="input-base mt-2" data-testid={`${tab}-register-password`}
                />
              </div>
              <button
                disabled={loading} type="submit"
                data-testid={`${tab}-register-submit`}
                className="btn-primary w-full flex items-center justify-center gap-2"
                style={{ background: `linear-gradient(90deg, ${active.accent} 0%, #8B5CF6 100%)`,
                         boxShadow: `0 8px 24px ${active.accent}55` }}
              >
                {loading ? "Creating…" : `Create ${(cfg?.label || active.label).toLowerCase()} account`} <ArrowRight size={16} weight="bold" />
              </button>
              <p className="text-[11px] text-muted text-center leading-relaxed">
                By creating an account you agree to CrewHQ terms.
              </p>
            </form>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}
